import React, { useState, useEffect } from 'react';
import { Search, Star, AlertCircle, Info } from 'lucide-react';
import { GlossaryTerm, GlossarySourceCategory } from '../../types/glossary';
import { glossaryService } from '../../services/glossaryService';
import { glossaryCategoryService } from '../../services/glossaryCategoryService';
import { useAuth } from '../../hooks/useAuth';
import { TermOfTheDay } from './TermOfTheDay';
import { ChromeExtension } from './ChromeExtension';
import { CategorySelector } from './CategorySelector';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { toast } from '../ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';

/**
 * Compact, modern component for displaying glossary terms in a condensed table format
 */
export const CompactGlossaryList: React.FC = () => {
  const { user } = useAuth();
  const [terms, setTerms] = useState<GlossaryTerm[]>([]);
  const [filteredTerms, setFilteredTerms] = useState<GlossaryTerm[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [favoritedTerms, setFavoritedTerms] = useState<Record<string, boolean>>({});
  const [totalTerms, setTotalTerms] = useState(0);
  const [selectedTerm, setSelectedTerm] = useState<GlossaryTerm | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [categories, setCategories] = useState<GlossarySourceCategory[]>([]);

  // Fetch categories for name mapping
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const allCategories = await glossaryCategoryService.getActiveCategories();
        setCategories(allCategories);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    fetchCategories();
  }, []);

  // Fetch all glossary terms
  useEffect(() => {
    const fetchTerms = async () => {
      try {
        setLoading(true);
        const allTerms = await glossaryService.getGlossaryTerms();
        setTerms(allTerms);
        setFilteredTerms(allTerms);
        setTotalTerms(allTerms.length);
        setError(null);
      } catch (err) {
        console.error('Error fetching glossary terms:', err);
        setError('Failed to load glossary terms');
      } finally {
        setLoading(false);
      }
    };

    fetchTerms();
  }, []);

  // Check which terms are favorited
  useEffect(() => {
    const checkFavoritedTerms = async () => {
      if (!user || terms.length === 0) return;

      try {
        const favorites = await glossaryService.getFavorites(user.uid);
        const favoritedMap: Record<string, boolean> = {};
        terms.forEach(term => {
          favoritedMap[term.id] = favorites.some(fav => fav.termId === term.id);
        });
        setFavoritedTerms(favoritedMap);
      } catch (err) {
        console.error('Error checking favorited terms:', err);
      }
    };

    checkFavoritedTerms();
  }, [user, terms]);

  // Filter terms based on search query and selected category
  useEffect(() => {
    let filtered = terms;

    if (selectedCategoryId) {
      filtered = filtered.filter(term => term.categoryId === selectedCategoryId);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(term =>
        term.term.toLowerCase().includes(query) ||
        term.content.toLowerCase().includes(query)
      );
    }
    
    setFilteredTerms(filtered);
  }, [searchQuery, selectedCategoryId, terms]);

  // Toggle term favorite status
  const toggleFavorite = async (termId: string) => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to manage favorites",
        variant: "destructive",
      });
      return;
    }

    try {
      const isFavorited = favoritedTerms[termId];
      
      if (isFavorited) {
        await glossaryService.removeFavorite(termId, user.uid);
        setFavoritedTerms(prev => ({ ...prev, [termId]: false }));
        toast({
          title: "Removed from Favorites",
          description: "Term has been removed from your favorites",
          variant: "success",
          className: "bg-green-600 text-white border-green-700",
          duration: 2000,
        });
      } else {
        const categories = await glossaryService.getCategories(user.uid);
        let defaultCategory = categories.find(cat => cat.name === 'My Favorites');
        
        if (!defaultCategory) {
          const categoryId = await glossaryService.createCategory(
            'My Favorites',
            user.uid,
            user.displayName || 'Unknown User',
            user.photoURL || undefined
          );
          const updatedCategories = await glossaryService.getCategories(user.uid);
          defaultCategory = updatedCategories.find(cat => cat.id === categoryId);
          
          if (!defaultCategory) {
            throw new Error('Failed to create default category');
          }
        }
        
        await glossaryService.addToFavorites(termId, user.uid, defaultCategory.id);
        setFavoritedTerms(prev => ({ ...prev, [termId]: true }));
        toast({
          title: "Added to Favorites",
          description: "Term has been added to your favorites",
          variant: "success",
          className: "bg-green-600 text-white border-green-700",
          duration: 2000,
        });
      }
    } catch (error) {
      console.error('Error toggling favorite status:', error);
      toast({
        title: "Error",
        description: "Failed to update favorites",
        variant: "destructive",
        className: "bg-red-600 text-white border-red-700",
        duration: 2000,
      });
    }
  };

  // Get category name from ID
  const getCategoryName = (categoryId?: string): string => {
    if (!categoryId) return 'General';
    
    // First try to use the categoryName if it exists
    const term = terms.find(t => t.categoryId === categoryId);
    if (term?.categoryName) return term.categoryName;
    
    // Otherwise look up in categories array
    const category = categories.find(cat => cat.id === categoryId);
    return category?.name || categoryId;
  };

  // Open term definition modal
  const openTermDialog = (term: GlossaryTerm) => {
    setSelectedTerm(term);
    setIsDialogOpen(true);
  };

  return (
    <div className="px-4 md:px-8">
      {/* Featured components in a grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <TermOfTheDay />
        <ChromeExtension />
      </div>
      
      {/* Glossary Terms List */}
      <div className="mb-4">
        {/* Category Selector and Search bar */}
        <div className="flex flex-col gap-4 mb-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <CategorySelector
                selectedCategoryId={selectedCategoryId}
                onCategoryChange={setSelectedCategoryId}
              />
              <div className="text-gray-800 dark:text-gray-300">
                {selectedCategoryId
                  ? `${filteredTerms.length} ${getCategoryName(selectedCategoryId)} terms`
                  : `${totalTerms} total terms available`
                }
              </div>
            </div>
            
            <div className="relative flex-1 md:max-w-md">
              <Input
                type="text"
                placeholder="Search glossary terms..."
                className="pr-10 glossary-search bg-gray-100 dark:bg-gray-800 border-gray-400 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400"
                style={{
                  border: '2px solid var(--glossary-border)',
                  boxShadow: '0 0 0 1px rgba(99, 102, 241, 0.1)'
                }}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Error state */}
        {error && (
          <div className="bg-red-900/20 border border-red-700 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <p className="text-red-400">{error}</p>
            </div>
          </div>
        )}
        
        {/* Loading state */}
        {loading && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <div key={i} className="flex items-center space-x-4 p-3 border border-gray-700 rounded-lg">
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-4 w-16" />
              </div>
            ))}
          </div>
        )}
        
        {/* No results */}
        {!loading && filteredTerms.length === 0 && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 text-center">
            <p className="text-gray-400">
              {searchQuery 
                ? `No terms found matching "${searchQuery}"`
                : "No glossary terms available"}
            </p>
          </div>
        )}
        
        {/* Compact Terms Grid - Two Columns */}
        {!loading && filteredTerms.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredTerms.map((term, index) => (
              <div
                key={term.id}
                className="bg-gray-100 dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg overflow-hidden hover:bg-gray-200 dark:hover:bg-gray-700/50 cursor-pointer"
                onClick={() => openTermDialog(term)}
              >
                <div className="flex items-center px-4 py-3 gap-3">
                  {/* Favorite Star */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(term.id);
                    }}
                    className="h-8 w-8 p-0 hover:bg-gray-200 dark:hover:bg-gray-600 flex-shrink-0"
                  >
                    <Star
                      className={`h-4 w-4 ${
                        favoritedTerms[term.id]
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-400 hover:text-yellow-400'
                      }`}
                    />
                  </Button>
                  
                  {/* Term Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-gray-800 dark:text-white font-medium truncate">
                        {term.term}
                      </h3>
                      <span className="ml-2 px-2 py-1 text-xs bg-blue-600 text-white rounded-full flex-shrink-0">
                        {getCategoryName(term.categoryId)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Term Definition Modal */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500 dark:text-blue-400" />
              {selectedTerm?.term}
            </DialogTitle>
            <DialogDescription className="text-gray-600 dark:text-gray-400">
              Category: {selectedTerm ? getCategoryName(selectedTerm.categoryId) : 'General'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            <div
              className="prose dark:prose-invert max-w-none prose-sm text-gray-700 dark:text-gray-300"
              dangerouslySetInnerHTML={{
                __html: selectedTerm?.content.replace(/<\/p><p>/g, '</p><br><p>') || ''
              }}
            />
          </div>
          
          <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-300 dark:border-gray-700">
            <Button
              variant="outline"
              onClick={() => selectedTerm && toggleFavorite(selectedTerm.id)}
              className="flex items-center gap-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <Star
                className={`h-4 w-4 ${
                  selectedTerm && favoritedTerms[selectedTerm.id]
                    ? 'text-yellow-400 fill-yellow-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}
              />
              {selectedTerm && favoritedTerms[selectedTerm.id] ? 'Remove from Favorites' : 'Add to Favorites'}
            </Button>
            
            <Button
              onClick={() => setIsDialogOpen(false)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};